import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, X, ChevronUp, ChevronDown, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";

const DEFAULTS = {
  kicker: "Missions & Outreach",
  heading: "Globally Minded!",
  description:
    "A heart for the world — supporting and praying for those who carry hope to far places. These are the people and ministries we walk beside.",
  items: [],
};

export default function AdminMissions() {
  const [form, setForm] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/missions")
      .then((r) => setForm({ ...DEFAULTS, ...(r.data || {}) }))
      .finally(() => setLoading(false));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const resetField = (k) => setForm((f) => ({ ...f, [k]: DEFAULTS[k] }));

  const updateItem = (i, key, val) =>
    setForm((f) => {
      const next = [...f.items];
      next[i] = { ...next[i], [key]: val };
      return { ...f, items: next };
    });

  const addItem = () =>
    setForm((f) =>
      f.items.length >= 12
        ? f
        : { ...f, items: [...f.items, { name: "", description: "", url: "", image: "" }] },
    );

  const removeItem = (i) =>
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i),
    }));

  const moveItem = (i, dir) =>
    setForm((f) => {
      const next = [...f.items];
      const t = i + dir;
      if (t < 0 || t >= next.length) return f;
      [next[i], next[t]] = [next[t], next[i]];
      return { ...f, items: next };
    });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/missions", {
        ...form,
        items: form.items.filter((m) => m.name?.trim()),
      });
      toast.success("Missions saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20" data-testid="admin-missions-loading">
        <p className="text-ink-soft">Loading…</p>
      </div>
    );
  }

  return (
    <div className="bg-paper min-h-[80vh]" data-testid="admin-missions-page">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-16 pb-24">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-soft hover:text-terracotta mb-8"
          data-testid="admin-missions-back-link"
        >
          <ArrowLeft size={14} /> Dashboard
        </Link>

        <p className="text-xs uppercase tracking-[0.22em] text-terracotta mb-4">
          Missions
        </p>
        <h1 className="font-serif-display text-3xl lg:text-4xl text-ink tracking-tight">
          Globally Minded!
        </h1>
        <p className="mt-3 text-sm text-ink-soft max-w-2xl">
          A section on your Home page for the missions, ministries, and outreach you support. Use <code className="text-ink">*highlight*</code> and <code className="text-ink">_italic_</code> markup in the heading.
        </p>

        <div className="mt-12 space-y-8">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft">
                Small Kicker
              </label>
              <button
                type="button"
                onClick={() => resetField("kicker")}
                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-ink-soft hover:text-terracotta"
              >
                <RotateCcw size={12} /> Reset
              </button>
            </div>
            <input
              value={form.kicker}
              onChange={set("kicker")}
              data-testid="admin-missions-kicker"
              className="w-full bg-transparent border-b border-rule focus:border-ink py-3 text-sm text-ink outline-none"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft">
                Heading
              </label>
              <button
                type="button"
                onClick={() => resetField("heading")}
                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-ink-soft hover:text-terracotta"
              >
                <RotateCcw size={12} /> Reset
              </button>
            </div>
            <input
              value={form.heading}
              onChange={set("heading")}
              data-testid="admin-missions-heading"
              className="w-full bg-transparent border-b border-rule focus:border-ink py-3 text-2xl font-serif-display text-ink outline-none"
            />
            <p className="mt-2 text-xs text-ink-soft italic">
              *word* highlights in terracotta. _word_ italicizes.
            </p>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-ink-soft mb-2">
              Description
            </label>
            <textarea
              rows={4}
              value={form.description}
              onChange={set("description")}
              data-testid="admin-missions-description"
              className="w-full bg-paper border border-rule px-4 py-3 text-base text-ink leading-relaxed outline-none focus:border-ink"
            />
          </div>

          <div className="border-t border-rule pt-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-ink-soft">
                  Missions / Ministries (max 12)
                </p>
                <p className="text-xs text-ink-soft mt-1 normal-case tracking-normal">
                  Each becomes a card on your Home page.
                </p>
              </div>
              <button
                onClick={addItem}
                disabled={form.items.length >= 12}
                data-testid="admin-missions-add"
                className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] text-terracotta hover:text-ink disabled:opacity-40"
              >
                <Plus size={14} /> Add mission
              </button>
            </div>

            {form.items.length === 0 && (
              <p className="text-sm text-ink-soft italic">No missions added yet.</p>
            )}

            <div className="space-y-5">
              {form.items.map((m, i, arr) => (
                <div
                  key={i}
                  data-testid={`admin-missions-row-${i}`}
                  className="border border-rule p-5 bg-paper-2/30 relative"
                >
                  <div className="absolute top-3 right-3 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => moveItem(i, -1)}
                      disabled={i === 0}
                      data-testid={`admin-missions-up-${i}`}
                      className="p-1 border border-rule text-ink-soft hover:text-ink hover:border-ink disabled:opacity-30"
                      title="Move up"
                    >
                      <ChevronUp size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveItem(i, 1)}
                      disabled={i === arr.length - 1}
                      data-testid={`admin-missions-down-${i}`}
                      className="p-1 border border-rule text-ink-soft hover:text-ink hover:border-ink disabled:opacity-30"
                      title="Move down"
                    >
                      <ChevronDown size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => removeItem(i)}
                      data-testid={`admin-missions-remove-${i}`}
                      className="p-1 text-ink-soft hover:text-terracotta"
                      title="Remove"
                    >
                      <X size={16} />
                    </button>
                  </div>

                  <div className="space-y-3 pr-28">
                    <input
                      value={m.name || ""}
                      onChange={(e) => updateItem(i, "name", e.target.value)}
                      placeholder="Mission name (e.g. Compassion International)"
                      data-testid={`admin-missions-name-${i}`}
                      className="w-full bg-transparent border-b border-rule focus:border-ink py-2 text-lg font-serif-display text-ink outline-none"
                    />
                    <textarea
                      value={m.description || ""}
                      onChange={(e) => updateItem(i, "description", e.target.value)}
                      rows={2}
                      placeholder="Short description (optional)"
                      data-testid={`admin-missions-desc-${i}`}
                      className="w-full bg-paper border border-rule px-3 py-2 text-sm text-ink leading-relaxed outline-none focus:border-ink"
                    />
                    <input
                      value={m.url || ""}
                      onChange={(e) => updateItem(i, "url", e.target.value)}
                      placeholder="Link URL (optional, e.g. https://…)"
                      data-testid={`admin-missions-url-${i}`}
                      className="w-full bg-transparent border-b border-rule focus:border-ink py-2 text-sm text-ink outline-none"
                    />
                    <ImageUploader
                      value={m.image || ""}
                      onChange={(v) => updateItem(i, "image", v)}
                      label="Image (optional)"
                      testIdPrefix={`admin-missions-image-${i}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-6 border-t border-rule flex items-center gap-3">
            <button
              onClick={save}
              disabled={saving}
              data-testid="admin-missions-save-btn"
              className="px-6 py-3 bg-terracotta text-paper text-xs tracking-[0.18em] uppercase hover:bg-ink transition-colors disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save Missions"}
            </button>
            <Link
              to="/"
              target="_blank"
              rel="noreferrer"
              className="text-xs uppercase tracking-[0.18em] text-ink-soft hover:text-terracotta link-underline"
            >
              Preview ↗
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
